import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Header } from '@/components/custom/header/header';
import { Menu } from '@/components/custom/menu';
import CpfModal from '@/components/custom/cpf-modal';
import { useSession } from '@/hooks/useSession';
import PtsModal from '@/pages/users/PtsModal';
import ActionCableConsumer from '@/utils/actionCableConsumer';
import DuelNotification from '@/components/custom/duel-notification';
import NoClassroomModal from '@/components/custom/no-classroom-modal';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { LogosSeduc } from '@/components/custom/logos-seduc/LogosSeduc';
import PersonalDataModal from '@/components/custom/personal-data-modal';
import CpfConfirmationModal from '@/components/custom/cpf-confirmation-modal';
import { Dashboard } from '@/layouts/dashboard';
import { PlayfulNavContext } from '@/lib/playful-nav-context';
import { BottomBarInfantil } from '@/components/custom/bottom-bar-infantil';
import { BottomBarProfessor } from '@/components/custom/bottom-bar-professor';
import usePlayfulNav from '@/hooks/usePlayfulNav';
import { RegistrationPendingContext } from '@/lib/registration-pending-context';

const pageLayoutsWrapperVariants = cva('c-page-layouts-wrapper w-full mt-0', {
  variants: {
    hasCustomLogos: {
      true: 'mt-[68px] md:mt-0',
      false: 'mt-0'
    }
  },
  defaultVariants: {
    hasCustomLogos: false
  }
});

const PageLayoutsWrapper = React.forwardRef(({ className, hasCustomLogos, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(pageLayoutsWrapperVariants({ hasCustomLogos }), className)}
      {...props}
    />
  );
});
PageLayoutsWrapper.displayName = 'PageLayoutsWrapper';

export function PageLayouts(props) {
  const { layoutType, children } = props;
  const [showModal, setShowModal] = React.useState(false);
  const { session } = useSession();
  const [achievementData, setAchievementData] = React.useState(null);
  const [duelData, setDuelData] = React.useState(null);
  const [showPersonalDataModal, setShowPersonalDataModal] = React.useState(false);
  const [showCpfConfirmationModal, setShowCpfConfirmationModal] = React.useState(false);

  const reviwedData = session?.user?.reviewedInfos;

  //noclassroom modal
  const client = useClient();
  const currentUser = session?.user;
  const [show, setShow] = React.useState(false);
  const [studentClassrooms, setStudentClassrooms] = React.useState();
  const [verifiedUser, setVerifiedUser] = React.useState(null);
  // Indicam que a consulta atual terminou, não que o cadastro está correto.
  const [verificationChecked, setVerificationChecked] = React.useState(false);
  const [classroomsChecked, setClassroomsChecked] = React.useState(false);

  const renderCPF = currentUser?.cpfCnpj === null || currentUser?.cpfCnpj === '';
  const renderClassrooms = studentClassrooms?.length === 0 || studentClassrooms?.some(e => ['turma única', 'sem turma', 'sem escola'].includes(e.title.toLowerCase()) && !['sem escola', 'já sou formado'].includes(e.institution.name.toLowerCase()));

  const appName = process.env.REACT_APP_NAME;
  const allowCreateUser = process.env.REACT_APP_ALLOW_CREATE_USER === 'true';
  const hasCustomLogos = appName === 'canaleducacao' || appName === 'maiseducacao';
  const isMainLayout = layoutType === 'mainLayout';

  // Quem troca a sidebar por bottom bar (aluno e professor) vs quem fica na
  // sidebar (admin, tutor, secretariado): regra única no usePlayfulNav — o
  // perfil consulta o mesmo hook para não deixar o professor sem logout.
  const playfulNav = usePlayfulNav();
  const isStudent = currentUser?.profileNames?.[0] === 'Aluno';

  const needsVerification = isStudent && ['maiseducacao', 'canaleducacao'].includes(appName);
  const waitingForVerification = needsVerification && !verificationChecked;
  const waitingForClassrooms = isStudent && !allowCreateUser && !reviwedData && !classroomsChecked;
  const hasOpenRegistrationModal = show || showModal || showPersonalDataModal || showCpfConfirmationModal;
  // CpfModal demora 500 ms para abrir; a ausência do CPF já bloqueia o aviso.
  const missingRequiredCpf = process.env.REACT_APP_REQUIRED_CPF === 'true' && !!currentUser && !currentUser.cpfCnpj;

  // O aviso do simulado aguarda as consultas atuais e as modais cadastrais.
  const registrationPending = isMainLayout && !!session && (waitingForVerification || waitingForClassrooms || hasOpenRegistrationModal || missingRequiredCpf);

  React.useEffect(() => {
    const openModal = !reviwedData && currentUser?.profileNames[0] === 'Aluno' && (renderCPF || renderClassrooms) && !allowCreateUser;
    setShow(openModal);
  }, [studentClassrooms, currentUser, allowCreateUser]);

  async function getStudentClassroom(isCurrentRequest) {
    const { data, error } = await client.fetch('classrooms');
    if (!isCurrentRequest()) return;
    if (error) {
      toast.error('Erro ao buscar turmas');
    } else {
      setStudentClassrooms(data);
    }
    // Encerra a espera também no erro; a mensagem acima informa a falha.
    setClassroomsChecked(true);
  }

  async function checkVerifiedUser(isCurrentRequest) {
    const { data, error } = await client.fetch(`users/${currentUser.id}/verifications`);
    if (!isCurrentRequest()) return;
    if (error) {
      toast.error('Erro ao validar dados do usuário');
    } else {
      setVerifiedUser(data);
      if (currentUser?.profileNames[0] === 'Aluno' && data?.['cpf-verified'] === false) {
        if (appName === 'maiseducacao') setShowCpfConfirmationModal(true);
        if (appName === 'canaleducacao') setShowPersonalDataModal(true);
      }
    }
    // Processa a resposta e a abertura da modal antes de liberar o aviso.
    // No erro, encerra a espera mantendo a mensagem de falha acima.
    setVerificationChecked(true);
  }

  React.useEffect(() => {
    let active = true;
    const isCurrentRequest = () => active;

    // Uma atualização da sessão inicia uma nova espera, mesmo sem remontar o layout.
    setVerificationChecked(false);
    setClassroomsChecked(false);
    if (session) {
      if (currentUser?.profileNames[0] === 'Aluno' || currentUser?.profileNames[0] === 'Professor') {
        checkVerifiedUser(isCurrentRequest);
      }
      if (currentUser?.profileNames[0] === 'Aluno') {
        getStudentClassroom(isCurrentRequest);
      }
    }
    // Ignora respostas de consultas anteriores ou recebidas após desmontar o layout.
    return () => {
      active = false;
    };
  }, [session]);

  return (
    <PlayfulNavContext.Provider value={playfulNav}>
      <Dashboard>
        {isMainLayout && session && (
          <>
            {/* Na navegação lúdica o logo institucional já vive dentro do header
              (playfulLogo é o mesmo arquivo) — o banner duplicaria a marca. */}
            {!playfulNav && <LogosSeduc hasCustomLogos={hasCustomLogos} />}
            {!playfulNav && <Menu hasCustomLogos={hasCustomLogos} />}
            {/* Mesma barra, destinos diferentes: aluno tem lista própria
              (lúdica), professor projeta o adminMenu da sidebar. O else só é
              "professor" por eliminação (playfulNav ⇒ aluno ou professor), o
              que acopla este ponto ao hook: se um terceiro perfil lúdico
              entrar lá, ele cairia no adminMenu sem passar por aqui. O
              !currentUser mantém o predicado original e deixa a eliminação
              explícita — dentro deste bloco session.user sempre existe: ele
              só monta sob `session &&`, e os ActionCableConsumer abaixo leem
              session.user.* sem guarda. */}
            {playfulNav && (!currentUser || isStudent ? <BottomBarInfantil /> : <BottomBarProfessor />)}
            <Header
              hasCustomLogos={hasCustomLogos}
              playfulNav={playfulNav}
            />
            <NoClassroomModal
              renderCPF={renderCPF}
              renderClassrooms={renderClassrooms}
              show={show}
              setShow={setShow}
              currentUser={currentUser}
            />
            {showPersonalDataModal && (
              <PersonalDataModal
                show={showPersonalDataModal}
                setShow={setShowPersonalDataModal}
                currentUser={currentUser}
                verifiedUser={verifiedUser}
              />
            )}
            <CpfConfirmationModal
              showModal={showCpfConfirmationModal}
              setShowModal={setShowCpfConfirmationModal}
              currentUser={currentUser}
            />
            <CpfModal
              showModal={showModal}
              setShowModal={setShowModal}
            />
            <PtsModal
              achievementData={achievementData}
              setAchievementData={setAchievementData}
            />
            <DuelNotification
              duelData={duelData}
              setDuelData={setDuelData}
            />
            <ActionCableConsumer
              channel="UserMilestoneChannel"
              setObjectChannel={setAchievementData}
              channelKey={'user_id'}
              channelId={session.user.id}
            />
            <ActionCableConsumer
              channel="DuelNotificationsChannel"
              setObjectChannel={setDuelData}
              token={session.user.authToken}
            />
          </>
        )}

        {/* Sem banner no playful → sem mt-[68px] compensatório no mobile. */}
        <RegistrationPendingContext.Provider value={registrationPending}>
          <PageLayoutsWrapper hasCustomLogos={hasCustomLogos && isMainLayout && !playfulNav}>{children}</PageLayoutsWrapper>
        </RegistrationPendingContext.Provider>
      </Dashboard>
    </PlayfulNavContext.Provider>
  );
}
